/**
 * Initialize Auth from persist:userdb or Redux store
 * Called on app launch to restore auth state.
 * Redux store is primary; falls back to persist:userdb when store is not yet rehydrated.
 */

import { store } from "../../store";
import { SET_AUTH_DATA } from "../../db/actionTypes/otpValidation";
import { getAuthDataFromPersistUser } from "./authStorage";
import { getAccessTokenFromStore, getRefreshTokenFromStore } from "./tokenManager";

/**
 * Initializes auth state on app launch
 * Priority: 1. Redux store (from persist:userdb via Redux persist), 2. persist:userdb direct read
 * Should be called after Redux store is ready (after PersistGate)
 */
export function initializeAuthFromStorage(): void {
  try {
    const storeAccessToken = getAccessTokenFromStore();
    const storeRefreshToken = getRefreshTokenFromStore();

    // If tokens exist in Redux store, use them (store is source of truth)
    // Redux persist should have already rehydrated from persist:userdb
    if (storeAccessToken || storeRefreshToken) {
      console.log("🟢 initializeAuthFromStorage: Using tokens from Redux store", {
        hasAccessToken: !!storeAccessToken,
        hasRefreshToken: !!storeRefreshToken,
      });
      return; // Store already has tokens, no need to sync
    }

    // Store doesn't have tokens, try to get from persist:userdb
    const persistUserData = getAuthDataFromPersistUser();
    if (persistUserData?.accessToken || persistUserData?.refreshToken) {
      console.log("🟢 initializeAuthFromStorage: Initializing from persist:userdb", {
        hasAccessToken: !!persistUserData.accessToken,
        hasRefreshToken: !!persistUserData.refreshToken,
      });

      // Store in Redux store (which will persist to persist:userdb via Redux persist)
      store.dispatch({
        type: SET_AUTH_DATA,
        payload: {
          accessToken: persistUserData.accessToken || "",
          refreshToken: persistUserData.refreshToken || "",
          user: persistUserData.user || {},
        },
      });
    } else {
      console.log("🔵 initializeAuthFromStorage: No auth data found in store or persist:userdb");
    }
  } catch (error) {
    console.error("🔴 initializeAuthFromStorage: Error initializing auth from storage", error);
  }
}

